import React from 'react';

/**
 * ErrorBoundary — Catches React render errors and prevents the dreaded white screen.
 *
 * Special handling:
 *  - ChunkLoadError (happens after a redeploy when the browser has a stale bundle):
 *    automatically forces a fresh reload once to pick up the new bundle.
 *  - Generic render errors: shows a recovery UI with auto-retry countdown.
 *  - Auto-retry every 5 seconds (resets error state) so transient errors self-heal.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, countdown: 5 };
    this._retryTimer = null;
    this._countdownTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, countdown: 5 };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Component crash:', error, errorInfo);

    // Detect stale-bundle errors (after a redeploy). Force one hard reload to fetch the new bundle.
    const msg = error?.message || '';
    const name = error?.name || '';
    const isChunkError =
      name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg);

    if (isChunkError && !sessionStorage.getItem('__chunk_reload_attempted')) {
      sessionStorage.setItem('__chunk_reload_attempted', '1');
      // Hard reload, bypass cache
      window.location.reload();
      return;
    }

    // Start auto-retry countdown
    this._startAutoRetry();
  }

  componentWillUnmount() {
    this._clearTimers();
  }

  _clearTimers = () => {
    if (this._retryTimer) clearTimeout(this._retryTimer);
    if (this._countdownTimer) clearInterval(this._countdownTimer);
    this._retryTimer = null;
    this._countdownTimer = null;
  };

  _startAutoRetry = () => {
    this._clearTimers();
    this._countdownTimer = setInterval(() => {
      this.setState((s) => ({ countdown: Math.max(0, (s.countdown ?? 5) - 1) }));
    }, 1000);
    this._retryTimer = setTimeout(() => {
      this._clearTimers();
      this.setState({ hasError: false, error: null, countdown: 5 });
    }, 5000);
  };

  _handleReload = () => {
    this._clearTimers();
    // Clear the chunk-reload guard so a fresh chunk error can trigger a reload again next time
    sessionStorage.removeItem('__chunk_reload_attempted');
    window.location.reload();
  };

  _handleRetry = () => {
    this._clearTimers();
    this.setState({ hasError: false, error: null, countdown: 5 });
  };

  render() {
    if (this.state.hasError) {
      const { countdown } = this.state;
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
          data-testid="error-boundary-root"
        >
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '520px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#fee2e2',
                margin: '0 auto 1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
              }}
              aria-hidden="true"
            >
              ⚠
            </div>
            <h2 style={{ color: '#991b1b', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>
              Esta sección tuvo un problema
            </h2>
            <p style={{ color: '#7f1d1d', margin: '0 0 1rem 0', fontSize: '0.95rem' }}>
              Tu información está segura. Reintentando automáticamente en{' '}
              <strong>{countdown}</strong>s...
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this._handleRetry}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.65rem 1.25rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                }}
                data-testid="error-boundary-retry-btn"
              >
                Reintentar ahora
              </button>
              <button
                onClick={this._handleReload}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.65rem 1.25rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                }}
                data-testid="error-boundary-reload-btn"
              >
                Recargar página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
